import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { PasswordInput, PasswordStrength } from "../components/PasswordInput";

const BG = "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxneW0lMjB3b3Jrb3V0JTIwd2VpZ2h0c3xlbnwwfHx8fDE3ODUzMjQ1NzJ8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage({ inviteToken = null, inviteEmail = "", inviteCode = "" }) {
  const { loginUser } = useAuth();
  const [mode, setMode] = useState(inviteToken || inviteCode ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: inviteEmail, password: "", code: inviteCode });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setEmailEnabled(!!data.email_enabled)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setInfo(data.message);
      } else {
        const path = mode === "login" ? "/auth/login" : "/auth/register";
        const payload = mode === "login"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, password: form.password,
              invite_token: inviteToken, invite_code: form.code || null };
        const { data } = await api.post(path, payload);
        loginUser(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-end sm:items-center justify-center"
      style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-full max-w-md p-6 pb-12 sm:pb-6">
        <h1 className="font-display text-6xl text-white leading-none">GYM<span className="text-[#FF3B30]">TRACK</span></h1>
        <p className="text-[#B0B0B4] text-sm mt-1 mb-8">
          {inviteToken || inviteCode ? "Tu es invité à rejoindre l'équipe ! Crée ton compte 👇" : "Hypertrophie · Bloc A/B · RIR 1-2"}
        </p>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input data-testid="register-name-input" className="auth-input" placeholder="Prénom" required
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          )}
          <input data-testid="login-email-input" type="email" className="auth-input" placeholder="Email" required
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {mode !== "forgot" && (
            <PasswordInput testId="login-password-input" placeholder="Mot de passe"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          )}
          {mode === "register" && <PasswordStrength password={form.password} />}
          {mode === "register" && (
            <input data-testid="register-invite-code-input" className="auth-input uppercase" placeholder="Code d'invitation (optionnel)"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          )}
          {error && <p data-testid="auth-error" className="text-[#FF3B30] text-sm">{error}</p>}
          {info && <p data-testid="auth-info" className="text-green-400 text-sm">{info}</p>}
          <button data-testid="login-submit-button" disabled={loading}
            className="w-full h-12 bg-[#FF3B30] text-white font-display text-2xl tracking-wide hover:bg-[#e0322a] active:scale-[0.98] transition-colors disabled:opacity-50">
            {loading ? "..." : mode === "login" ? "SE CONNECTER" : mode === "forgot" ? "ENVOYER LE LIEN" : "CRÉER MON COMPTE"}
          </button>
        </form>
        {mode === "login" && (emailEnabled ? (
          <button data-testid="forgot-password-button"
            onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
            className="mt-4 mr-4 text-[#B0B0B4] text-sm underline underline-offset-4 hover:text-white transition-colors">
            Mot de passe oublié ?
          </button>
        ) : (
          <p data-testid="forgot-password-contact" className="mt-4 text-[#B0B0B4] text-sm">
            Mot de passe oublié ? Contacte Mathieu pour le réinitialiser.
          </p>
        ))}
        <button data-testid="toggle-auth-mode-button"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setInfo(""); }}
          className="mt-4 text-[#B0B0B4] text-sm underline underline-offset-4 hover:text-white transition-colors">
          {mode === "login" ? "Pas de compte ? Inscris-toi" : mode === "forgot" ? "← Retour à la connexion" : "Déjà un compte ? Connecte-toi"}
        </button>
      </div>
    </div>
  );
}
